/**
 * Route / path resolution against segment adjacency graphs.
 * Maps input coordinates to graph vertices by exact adjacent-point hash,
 * then fills gaps between consecutive vertices via BFS.
 */

import {
  canonicalAdjacentPointHash,
  formatAdjacentPointHash,
  parseAdjacentPointHash,
} from './adjacent-point-hash.util';

const EARTH_RADIUS_M = 6_371_000;

/** Haversine distance in meters (WGS84). Args: lat1, lng1, lat2, lng2. */
export function haversineDistanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}

export interface SegmentGraph {
  adjacency: Map<string, string[]>;
  vertexMap: Map<string, [number, number]>;
}

/** Build undirected adjacency + vertex coordinates from segment `adjacentPoints` records. */
export function buildSegmentGraphFromAdjacentPoints(
  adjacentPointsList: ReadonlyArray<Record<string, string[]>>,
): SegmentGraph {
  const adjacency = new Map<string, string[]>();
  const vertexMap = new Map<string, [number, number]>();

  const link = (a: string, b: string): void => {
    const na = adjacency.get(a) ?? [];
    if (!na.includes(b)) na.push(b);
    adjacency.set(a, na);
  };

  for (const adj of adjacentPointsList) {
    for (const [haRaw, neighbors] of Object.entries(adj)) {
      const ha = canonicalAdjacentPointHash(haRaw);
      if (!ha) continue;
      const parsedA = parseAdjacentPointHash(ha);
      if (parsedA) vertexMap.set(ha, parsedA);
      const nList = Array.isArray(neighbors) ? neighbors : [];
      for (const hbRaw of nList) {
        if (typeof hbRaw !== 'string') continue;
        const hb = canonicalAdjacentPointHash(hbRaw);
        if (!hb) continue;
        const parsedB = parseAdjacentPointHash(hb);
        if (parsedB) vertexMap.set(hb, parsedB);
        link(ha, hb);
        link(hb, ha);
      }
    }
  }

  return { adjacency, vertexMap };
}

/** BFS shortest vertex path; returns [fromHash, ..., toHash] or null. */
export function findPathInAdjacencyGraph(
  adjacency: ReadonlyMap<string, readonly string[]>,
  fromHash: string,
  toHash: string,
): string[] | null {
  if (fromHash === toHash) return [fromHash];
  const visited = new Set<string>([fromHash]);
  const queue: { hash: string; path: string[] }[] = [{ hash: fromHash, path: [fromHash] }];
  while (queue.length > 0) {
    const { hash, path } = queue.shift()!;
    for (const n of adjacency.get(hash) ?? []) {
      if (visited.has(n)) continue;
      visited.add(n);
      const newPath = [...path, n];
      if (n === toHash) return newPath;
      queue.push({ hash: n, path: newPath });
    }
  }
  return null;
}

/**
 * Resolve WGS84 input to a graph vertex by exact adjacent-point hash only.
 * No spatial snap radius — nearby foreign vertices must not be pulled in.
 */
export function snapRoutePointToVertexHash(
  lng: number,
  lat: number,
  vertexMap: ReadonlyMap<string, [number, number]>,
): string | null {
  const exact = formatAdjacentPointHash(lng, lat);
  return vertexMap.has(exact) ? exact : null;
}

export class RoutePathResolutionError extends Error {
  constructor(
    readonly code: 'snap' | 'path',
    readonly detail: string,
  ) {
    super(detail);
    this.name = 'RoutePathResolutionError';
  }
}

/**
 * Map each input point to an exact graph vertex, then expand consecutive pairs
 * via direct edges or BFS. Returns ordered vertex hashes along the resolved route.
 */
export function expandRoutePointsToVertexPath(
  points: [number, number][],
  vertexMap: ReadonlyMap<string, [number, number]>,
  adjacency: ReadonlyMap<string, readonly string[]>,
): string[] {
  if (points.length < 2) return [];

  const snapped: string[] = [];
  for (const [lng, lat] of points) {
    const hash = snapRoutePointToVertexHash(lng, lat, vertexMap);
    if (!hash) {
      throw new RoutePathResolutionError(
        'snap',
        `No graph vertex at exact hash for [${lng},${lat}]`,
      );
    }
    if (snapped.length > 0 && snapped[snapped.length - 1] === hash) continue;
    snapped.push(hash);
  }

  if (snapped.length < 2) return snapped;

  const expanded: string[] = [snapped[0]];
  for (let i = 1; i < snapped.length; i++) {
    const from = expanded[expanded.length - 1];
    const to = snapped[i];
    if (from === to) continue;

    const neighbors = adjacency.get(from) ?? [];
    if (neighbors.includes(to)) {
      expanded.push(to);
      continue;
    }

    const path = findPathInAdjacencyGraph(adjacency, from, to);
    if (!path) {
      throw new RoutePathResolutionError(
        'path',
        `No connected path between vertices ${from} and ${to}`,
      );
    }
    expanded.push(...path.slice(1));
  }

  return expanded;
}

export interface SegmentAdjacentPointsInput {
  id: string;
  adjacentPoints?: Record<string, string[]> | null;
}

/** Endpoint vertex hashes from a segment's `adjacent_points` keys. */
export function segmentEndpointHashes(
  adjacentPoints?: Record<string, string[]> | null,
): string[] {
  const endpoints: string[] = [];
  for (const haRaw of Object.keys(adjacentPoints ?? {})) {
    const ha = canonicalAdjacentPointHash(haRaw);
    if (ha) endpoints.push(ha);
  }
  return endpoints;
}

/**
 * Segment IDs whose `adjacent_points` endpoint keys both lie on the resolved vertex path.
 * A segment A→B is included only when both A and B are on the path (hash match).
 */
export function resolveVertexPathToSegmentIds(
  segments: ReadonlyArray<SegmentAdjacentPointsInput>,
  vertexPath: readonly string[],
): string[] {
  if (vertexPath.length === 0) return [];

  const pathVertexSet = new Set(vertexPath);
  const pathIndex = new Map(vertexPath.map((hash, index) => [hash, index]));
  const segmentIds: string[] = [];
  const seen = new Set<string>();
  const minPathIndexBySegmentId = new Map<string, number>();

  for (const seg of segments) {
    const endpoints = segmentEndpointHashes(seg.adjacentPoints);
    if (endpoints.length < 2) continue;
    if (!endpoints.every((hash) => pathVertexSet.has(hash))) continue;
    if (seen.has(seg.id)) continue;
    seen.add(seg.id);
    segmentIds.push(seg.id);
    minPathIndexBySegmentId.set(
      seg.id,
      Math.min(...endpoints.map((h) => pathIndex.get(h) ?? Number.MAX_SAFE_INTEGER)),
    );
  }

  segmentIds.sort(
    (a, b) =>
      (minPathIndexBySegmentId.get(a) ?? Number.MAX_SAFE_INTEGER) -
      (minPathIndexBySegmentId.get(b) ?? Number.MAX_SAFE_INTEGER),
  );

  return segmentIds;
}
